<script lang="ts">
import Vue from 'vue';
import * as Sentry from '@sentry/vue';
import App from '@/App.vue';
import { clearEnhancedAuthState } from '@/utils/auth-state-persistence';
import { track } from '@/api/connectTrack';

/**
 * RootErrorBoundary — first-principles "never blank" guarantee.
 *
 * WHY: The blank-page bug was validated as a *client-state* fault — a stale/corrupt
 * persisted auth blob (e.g. `enhanced_auth_state`, `apollo-token`) sends boot down the
 * authenticated code path, and an uncaught throw there unmounts the whole Vue tree to a
 * blank page. A clean browser (no saved state) always renders fine. There was no error
 * boundary, so any such throw = permanent blank until the user manually cleared storage.
 *
 * WHAT: This component wraps <App/>. If any descendant — including App's own
 * setup/render/lifecycle — throws synchronously, Vue's `errorCaptured` fires here and we
 * render a minimal recovery screen instead of nothing, with a one-click "Reset session &
 * reload" that clears the stale client auth state and reloads onto the clean logged-out
 * path (the same state the working clean session has).
 *
 * SAFETY: Zero behavior change on the happy path (renders <App/> verbatim). The fallback
 * uses plain HTML + inline styles — no Vuetify — so it still renders even if the UI
 * framework itself is implicated. Recovery is user-triggered here (no auto-reload), so it
 * can never loop. The silent-blank case (nothing thrown) is handled by a loop-guarded
 * watchdog in main.ts.
 */
export default Vue.extend({
  name: 'RootErrorBoundary',

  data() {
    return { failed: false, detail: '' };
  },

  errorCaptured(err: Error, _vm: Vue, info: string) {
    // eslint-disable-next-line no-console
    console.error(
      '[RootErrorBoundary] Caught a fatal render error — showing recovery screen instead of a blank page:',
      err,
      info
    );
    // Returning false below STOPS propagation — Vue's global errorHandler (and so
    // Sentry's Vue integration) never sees boundary-caught fatals. Report explicitly,
    // or every one of these is invisible to us (validated: a founder-hit boundary at
    // /comms produced ZERO Sentry events). Best-effort — reporting must never throw.
    try {
      Sentry.captureException(err, {
        tags: { boundary: 'RootErrorBoundary' },
        extra: { vueInfo: info, path: window.location?.pathname }
      });
    } catch (e) {
      /* ignore */
    }
    // Connect analytics beacon (story A0): a boundary-caught fatal is the one
    // funnel exit Sentry sampling can miss. Fire-and-forget, never throws.
    try {
      track('fatal_error', {
        where: `${window.location?.pathname || ''}#${info || ''}`.slice(0, 120),
        message: String((err && err.message) || err || '').slice(0, 200)
      });
    } catch (e) {
      /* ignore */
    }
    this.failed = true;
    this.detail = (err && err.message) || String(err);
    return false; // stop propagation; without this the error would unmount the tree to blank
  },

  methods: {
    reload(): void {
      window.location.reload();
    },
    resetSession(): void {
      // Clear the stale client auth state that diverts boot onto the broken authed path.
      try {
        localStorage.removeItem('apollo-token');
      } catch (e) {
        /* ignore */
      }
      try {
        clearEnhancedAuthState();
      } catch (e) {
        /* ignore */
      }
      window.location.reload();
    },
    btnStyle(bg: string, border = 'transparent'): Record<string, string> {
      return {
        font: '600 14px Montserrat, sans-serif',
        color: bg === 'transparent' ? '#a5b4fc' : '#fff',
        background: bg,
        border: `1px solid ${border === 'transparent' ? '#6366f1' : border}`,
        borderRadius: '10px',
        padding: '10px 18px',
        cursor: 'pointer'
      };
    }
  },

  render(h) {
    if (!this.failed) return h(App);

    return h(
      'div',
      {
        style: {
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f172a',
          color: '#e2e8f0',
          padding: '24px',
          fontFamily: 'Montserrat, sans-serif'
        }
      },
      [
        h('div', { style: { maxWidth: '440px', textAlign: 'center' } }, [
          h('div', { style: { fontSize: '40px', marginBottom: '12px' } }, '🚧'),
          h(
            'h1',
            { style: { fontSize: '20px', fontWeight: '700', margin: '0 0 8px' } },
            "This page didn't load correctly"
          ),
          h(
            'p',
            {
              style: {
                fontSize: '14px',
                color: '#94a3b8',
                margin: '0 0 20px',
                lineHeight: '1.5'
              }
            },
            'A saved session on this browser got into a bad state. Reset it to continue — you may need to sign in again.'
          ),
          h('div', { style: { display: 'flex', gap: '10px', justifyContent: 'center' } }, [
            h(
              'button',
              { style: this.btnStyle('#6366f1'), on: { click: this.resetSession } },
              'Reset session & reload'
            ),
            h('button', { style: this.btnStyle('transparent'), on: { click: this.reload } }, 'Just reload')
          ]),
          // The actual error, so a screenshot of this screen IS the bug report
          // (pairs with the explicit Sentry capture in errorCaptured).
          this.detail
            ? h(
                'p',
                {
                  style: {
                    marginTop: '18px',
                    fontSize: '11px',
                    color: '#64748b',
                    fontFamily: 'monospace',
                    wordBreak: 'break-word',
                    lineHeight: '1.4'
                  }
                },
                this.detail
              )
            : null
        ])
      ]
    );
  }
});
</script>
